import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import {
  X,
  FileText,
  RotateCcw,
  Trash2,
  Loader2,
  CheckCircle,
  Sparkles,
} from "lucide-react";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  PrimaryBtn,
} from "../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import { useAppDispatch, useAppSelector } from "../../../Redux/Hooks";
import { updateBusinessDetailsAPI, getBusinessDetailsAPI } from "../../../Redux/ApiCalls/Auth/user";
import { setBusinessTermsAndConditions } from "../../../Redux/Slice/Auth/AuthSlice";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { DEFAULT_STUDIO_TERMS_TEXT } from "./DocumentTermsPdfModal";

export interface StudioTermsManageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const STORAGE_TITLE_KEY = "studio_terms_title";

export const getSavedTermsTitle = (): string => {
  try {
    const saved = localStorage.getItem(STORAGE_TITLE_KEY);
    if (saved && saved.trim()) return saved;
  } catch {}
  return "TERMS & CONDITIONS";
};

export const StudioTermsManageModal: React.FC<StudioTermsManageModalProps> = ({
  isOpen,
  onClose,
  onSaved,
}) => {
  const dispatch = useAppDispatch();
  const { business } = useAppSelector((state) => state.auth);

  const existingTerms = business?.terms_and_conditions || "";
  const isEditing = Boolean(existingTerms && existingTerms.trim());

  const [title, setTitle] = useState<string>(getSavedTermsTitle());
  const [description, setDescription] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTitle(getSavedTermsTitle());
      setDescription(business?.terms_and_conditions || "");
    }
  }, [isOpen, business?.terms_and_conditions]);

  if (!isOpen) return null;

  const handleInsertSample = () => {
    setDescription(DEFAULT_STUDIO_TERMS_TEXT);
  };

  const handleClear = () => {
    setDescription("");
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const cleanDesc = description.trim();
      const cleanTitle = title.trim() || "TERMS & CONDITIONS";

      // 1. Update backend Business.terms_and_conditions
      await dispatch(updateBusinessDetailsAPI({ terms_and_conditions: cleanDesc })).unwrap();

      // 2. Update Redux slice state
      dispatch(setBusinessTermsAndConditions(cleanDesc));

      // 3. Persist title in localStorage
      localStorage.setItem(STORAGE_TITLE_KEY, cleanTitle);

      // 4. Refresh business details to keep state in sync
      dispatch(getBusinessDetailsAPI());

      showSuccessToast(
        isEditing
          ? "Terms & Conditions updated successfully!"
          : "Terms & Conditions added successfully!"
      );

      onSaved?.();
      onClose();
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to save Terms & Conditions.");
    } finally {
      setIsSaving(false);
    }
  };

  return createPortal(
    <ModalOverlay onClick={() => !isSaving && onClose()}>
      <ModalShell
        style={{ maxWidth: 660 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroText>
              <HeroBadge>
                <Sparkles size={11} />
                Studio Profile
              </HeroBadge>
              <ModalTitle>
                {isEditing ? "Edit Terms & Conditions" : "Add Terms & Conditions"}
              </ModalTitle>
              <ModalSubtitle>
                Configure default studio terms for your quotations, orders, and invoices
              </ModalSubtitle>
            </HeroText>
            <CloseBtn
              type="button"
              onClick={() => !isSaving && onClose()}
              disabled={isSaving}
              aria-label="Close"
            >
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <ModalBody style={{ padding: "20px 24px" }}>
          {/* Field 1: Terms & Conditions Title */}
          <FormGroup>
            <Label>
              Terms & Condition Title <span style={{ color: "#ef4444" }}>*</span>
            </Label>
            <Input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. TERMS & CONDITIONS"
            />
            <FieldHint>Heading shown above the terms on generated documents and PDFs</FieldHint>
          </FormGroup>

          {/* Field 2: Terms & Conditions Description */}
          <FormGroup style={{ marginTop: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
              <Label style={{ margin: 0 }}>
                Terms & Condition Description <span style={{ color: "#ef4444" }}>*</span>
              </Label>
              <FieldHint style={{ margin: 0 }}>Point by point (line breaks preserved)</FieldHint>
            </div>
            <TextArea
              rows={10}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter terms and conditions (point by point)...&#10;1. Payment Terms...&#10;2. Deliverables..."
            />
          </FormGroup>

          {/* Quick Action Helpers */}
          <QuickActionsRow>
            <QuickBtn type="button" onClick={handleInsertSample}>
              <RotateCcw size={12} />
              <span>Insert Sample Photography Terms</span>
            </QuickBtn>
            {description && (
              <QuickBtn type="button" onClick={handleClear}>
                <Trash2 size={12} color="#dc2626" />
                <span style={{ color: "#dc2626" }}>Clear Description</span>
              </QuickBtn>
            )}
          </QuickActionsRow>

          {/* Modal Actions */}
          <ModalFooter>
            <CancelBtn
              type="button"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </CancelBtn>
            <PrimaryBtn
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 24px",
                borderRadius: "10px",
                minWidth: 150,
                justifyContent: "center",
              }}
            >
              {isSaving ? (
                <>
                  <Loader2 size={15} className="spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <CheckCircle size={15} />
                  <span>Save Terms & Condition</span>
                </>
              )}
            </PrimaryBtn>
          </ModalFooter>
        </ModalBody>
      </ModalShell>
    </ModalOverlay>,
    document.body
  );
};

// ── Styled Components ────────────────────────────────────────────────────────

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 13px;
  font-family: inherit;
  color: #111827;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 12px;
  line-height: 1.6;
  font-family: inherit;
  color: #111827;
  outline: none;
  resize: vertical;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  }
`;

const FieldHint = styled.span`
  font-size: 11px;
  color: #6b7280;
  margin-top: 4px;
`;

const QuickActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;

const QuickBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f3f4f6;
    border-color: #cbd5e1;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
`;

const CancelBtn = styled.button`
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default StudioTermsManageModal;
